import { useMutation } from '@tanstack/react-query'
import { useApi } from '@/lib/api-context'

/**
 * A confirmação pelo documento (docs/api/confirmacao-pelo-documento.md; T58): o documento traz os
 * e-mails mascarados dos cadastros com ele, a escolha manda o código e o código confirma, devolvendo
 * os dados pessoais. O mesmo contrato na anamnese pelo link (`/anamnesis/identity?t=`) e na recepção
 * (`/patients/identity`). A página nunca chama `fetch`.
 */

export type IdentityDocType = 'cpf' | 'passport' | 'rne' | 'crnm' | 'other'

export interface IdentityDocument {
  type: IdentityDocType
  number: string
  country?: string
}

export interface EmailOption {
  id: string
  /** "a•••@g•••.com": o endereço nunca vem inteiro. */
  masked: string
}

export interface IdentityOptions {
  emails: EmailOption[]
  message: string
}

export interface CodeSent {
  message: string
  expiresInMinutes: number
}

/** Os dados pessoais que o preenchimento rápido traz (RN-PAC-54). */
export interface Prefill {
  name: string
  birthDate: string | null
  docType: 'cpf' | null
  docNumber: string | null
  foreignDocument: { type: Exclude<IdentityDocType, 'cpf'>; number: string; country: string } | null
  rg: string | null
  gender: 'female' | 'male' | 'other' | 'not_provided'
  maritalStatus:
    'single' | 'married' | 'domestic_partnership' | 'separated' | 'divorced' | 'widowed' | null
  occupation: string | null
  email: string
  whatsapp: string | null
  address: {
    postalCode: string | null
    street: string | null
    number: string | null
    complement: string | null
    district: string | null
    city: string | null
    state: string | null
    country: string
  } | null
}

export interface Prefilled {
  prefillId: string
  prefill: Prefill
}

export interface EmailChanged {
  emailGrantId: string
  email: string
}

export interface IdentityPaths {
  lookup: string
  code: string
  verify: string
  email?: string
  emailVerify?: string
}

/** Os caminhos da anamnese pelo link, com o token em `?t=` (RN-ANA-31). */
export function anamnesisIdentityPaths(token: string): Required<IdentityPaths> {
  const t = `?t=${encodeURIComponent(token)}`
  return {
    lookup: `/anamnesis/identity${t}`,
    code: `/anamnesis/identity/code${t}`,
    verify: `/anamnesis/identity/verify${t}`,
    email: `/anamnesis/identity/email${t}`,
    emailVerify: `/anamnesis/identity/email/verify${t}`,
  }
}

/** Os caminhos da recepção (RN-PAC-58). */
export const RECEPTION_IDENTITY_PATHS: IdentityPaths = {
  lookup: '/patients/identity',
  code: '/patients/identity/code',
  verify: '/patients/identity/verify',
}

/** As cinco chamadas de um lugar. `document` nulo só na anamnese (vale o do cadastro). */
export function useIdentityActions(paths: IdentityPaths) {
  const api = useApi()
  return {
    lookup: useMutation({
      mutationFn: (document: IdentityDocument | null) =>
        api.post<IdentityOptions>(paths.lookup, document ? { document } : {}),
    }),
    code: useMutation({
      mutationFn: (input: { document: IdentityDocument | null; emailId: string }) =>
        api.post<CodeSent>(paths.code, withDocument(input)),
    }),
    verify: useMutation({
      mutationFn: (input: { document: IdentityDocument | null; emailId: string; code: string }) =>
        api.post<Prefilled>(paths.verify, withDocument(input)),
    }),
    emailCode: useMutation({
      mutationFn: (input: {
        document: IdentityDocument | null
        prefillId: string
        email: string
      }) => api.post<CodeSent>(paths.email ?? '', withDocument(input)),
    }),
    emailVerify: useMutation({
      mutationFn: (input: {
        document: IdentityDocument | null
        prefillId: string
        email: string
        code: string
      }) => api.post<EmailChanged>(paths.emailVerify ?? '', withDocument(input)),
    }),
  }
}

/** Sem documento, a chave não vai (a anamnese usa o do cadastro). */
function withDocument<T extends { document: IdentityDocument | null }>(input: T) {
  const { document, ...rest } = input
  return document ? { document, ...rest } : rest
}

export type IdentityActions = ReturnType<typeof useIdentityActions>
